import {
  makeStyles,
  TextField,
  Button,
  ThemeProvider,
  createTheme,
  withStyles,
} from "@material-ui/core";
import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { COLORS } from "../../../constant/colors";
import { getClientPolicy } from "../../../api/godp";
import moment from "moment";
import ErrorSvg from "../../../assets/eTriage/landing/error.svg";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import "moment/locale/zh-hk";
import "moment/locale/th";

const useStyles = makeStyles((theme) => ({
  validationContainer: {
    backgroundColor: COLORS.white,

    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: "30px 20px",
    display: "flex",
    flexDirection: "column",
  },
  itemRow: {
    paddingTop: 15,

    "& *": {
      borderColor: COLORS.fwdGrey,
    },
  },
  inputTextfield: {
    width: "100%",
  },
  goButtonText: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "20px",
  },
  goButton: {
    background: COLORS.fwdOrange,
    color: COLORS.white,
    height: 50,
    width: "100%",
    borderRadius: 8,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      background: COLORS.fwdOrange20,
    },
    "&:disabled": {
      background: COLORS.fwdOrange20,
      color: COLORS.white,
    },
  },
  errorMsgContainer: {
    display: "flex",
    flexDirection: "row",
    paddingTop: 10,
  },
  errorSvg: {
    width: 24,
    height: 24,
    marginRight: 7,
  },
  errorMsg: {
    color: COLORS.fwdAlertRed,
    fontSize: 14,
    fontWeight: 450,
    lineHeight: "21px",
  },


  isCustomerFormTitle: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '25px',
    color: COLORS.fwdDarkGreen,
    marginBottom: 15
  },
  isCustomerFormSelectButtonBox: {
    border: '1px solid',
    borderColor: COLORS.fwdGrey,
    borderRadius: 8,
    padding: 16,
    color: COLORS.fwdOrange,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '20px',
    textAlign: 'center',
    cursor: 'pointer'
  }
}));

const CustomerCSSTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: COLORS.fwdDarkGreen,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: COLORS.fwdGrey,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: COLORS.fwdGrey,
      },
      "&:hover fieldset": {
        borderColor: COLORS.fwdGrey,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.fwdGrey,
      },
    },
    "& .MuiFormLabel-root.Mui-error": {
      color: COLORS.fwdDarkGreen,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.fwdAlertRed,
    },
  },
})(TextField);

const keyboardDatePickerTheme = createTheme({
  palette: {
    primary: { main: COLORS.fwdOrange, contrastText: "#fff" },
    secondary: { main: COLORS.fwdOrange20 },
  },
});
const CustomerCSSKeyboardDatePicker = withStyles({
  root: {
    "& label.Mui-focused": {
      color: COLORS.fwdDarkGreen,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: COLORS.fwdGrey,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: COLORS.fwdGrey,
      },
      "&:hover fieldset": {
        borderColor: COLORS.fwdGrey,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.fwdGrey,
      },
    },
    "& .MuiFormLabel-root.Mui-error": {
      color: COLORS.fwdDarkGreen,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.fwdAlertRed,
    },
    "& .MuiPickersToolbar-toolbar": {
      backgroundColor: COLORS.fwdOrange,
    },
  },
})(DatePicker);

moment.locale("en");
const localeMap = {
  en: "en",
  tc: "zh-hk",
  th: "th",
};

function ClientValidationForm({ validationSuccess, isCustomerResponse, checkPolicy, allowNonCustomer }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [id, setId] = useState("");
  const [dob, setDob] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentBu = process.env.REACT_APP_BU ?? "hk";

  moment.locale(localeMap[i18n.language]);


  const validateClient = async () => {
    const clientId = id;

    const formattedDate =
      currentBu == "th" || currentBu == "my"
        ? moment(dob).format("YYYYMMDD")
        : moment(dob)
          .utcOffset(0, false)
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .format("x");

    getClientPolicy(clientId, formattedDate)
      .then((response) => {
        const clientInfo = response;
        if (clientInfo.hasPolicy && clientInfo.clientId) {
          if (process.env.REACT_APP_STORE_POLICY_DATA == "true") {
            validationSuccess(clientInfo.clientId, clientInfo);
          } else {
            validationSuccess(clientInfo.clientId, {});
          }
        } else {
          setIsError(true);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  const _renderPolicyValidationForm = () => {
    return (
      <>
        <div>
          {t("eTriageLanding.validation.pleaseValidateYourPolicyFirst")}
        </div>

        <div className={classes.itemRow}>
          <CustomerCSSTextField
            label={t(`eTriageLanding.validation.${currentBu}.idDocument`)}
            variant="outlined"
            className={classes.inputTextfield}
            value={id}
            onChange={(e) => {
              setIsError(false);
              setId(e.target.value);
            }}
            error={isError}
          />
        </div>
        <div className={classes.itemRow}>
          <MuiPickersUtilsProvider
            libInstance={moment}
            utils={MomentUtils}
            locale={i18n.language}
          >
            {" "}
            <ThemeProvider theme={keyboardDatePickerTheme}>
              <CustomerCSSKeyboardDatePicker
                fullWidth
                autoOk
                variant="inline"
                inputVariant="outlined"
                label={t("eTriageLanding.validation.dateOfBirth")}
                format="DD/MM/yyyy"
                disableFuture
                openTo="year"
                views={["year", "month", "date"]}
                value={dob}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={(date) => {
                  setIsError(false);
                  setDob(date);
                }}
                error={isError}
              />{" "}
            </ThemeProvider>
          </MuiPickersUtilsProvider>
        </div>

        {isError && (
          <div className={classes.errorMsgContainer}>
            <img src={ErrorSvg} className={classes.errorSvg} />
            <div className={classes.errorMsg}>
              {t("eTriageLanding.validation.errorMsg")}
            </div>
          </div>
        )}
        <div className={classes.itemRow}>
          <Button
            disabled={!(id != "" && dob != null) || isLoading}
            className={classes.goButton}
            onClick={() => {
              setIsLoading(true);
              validateClient();
            }}
          >
            <div className={classes.goButtonText}>
              {t("eTriageLanding.validation.go")}
            </div>
          </Button>
        </div>
      </>
    )

  };


  const _renderIsCustomerSelectForm = () => {
    return (<>
      <div className={classes.isCustomerFormTitle}>
        {t("eTriageLanding.validation.isCustomerTitle")}
      </div>
      <div className={classes.itemRow} onClick={() => { isCustomerResponse(true) }}>
        <div className={classes.isCustomerFormSelectButtonBox}>
          <div>{t("eTriageLanding.validation.fwdCustomer")}</div>
        </div>

      </div>
      <div className={classes.itemRow} onClick={() => { isCustomerResponse(false) }}>
        <div className={classes.isCustomerFormSelectButtonBox}>
          <div>{t("eTriageLanding.validation.nonCustomer")}</div>
        </div>

      </div>
    </>);
  };

  return (
    <>
      <div component="popupover" className={classes.validationContainer}>
        {checkPolicy && _renderPolicyValidationForm()}
        {allowNonCustomer && _renderIsCustomerSelectForm()}
      </div>
    </>
  );
}

export default ClientValidationForm;
